/**
 * Calendar (`/{ws}/calendar`, logic/calendar): Google Calendar's day / week / month views in my time zone — the days
 * on screen, each day's timed events laid out side by side (`layoutDay`) and its all-day events, "today" and
 * prev/next navigation — plus creating, editing, cancelling and answering events. Realtime `calendar.event.*`
 * events keep the visible range live. Online only; edits refetch the range when refused.
 */
import type { CalendarEvent, CalendarInfo, CreateEventRequest, EventOccurrence, PersistentEvent, RsvpStatus, UpdateEventRequest } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { DAY, addDays, isPast, defaultReminderMinutes, eventProblems, layoutDay, monthGrid, occurrenceReminder, occurrenceResponse, occurrencesOf, sameDay, sameRule, startOfDay, weekDays, withOccurrenceResponse, withOverride } from "../logic/calendar.ts";
import type { AppPasswordRequest } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type CalendarMode = "day" | "week" | "month";

/** One occurrence placed on screen. */
export type CalendarItem = {
  key: string;
  event: CalendarEvent;
  start: number;
  end: number;
  /** Day/week columns: side-by-side position among overlapping events. */
  column: number;
  columns: number;
  /** Minutes from the day's start, and length in minutes (clipped to the day). */
  top: number;
  height: number;
  /** My answer ("declined" events are drawn faded, like Google); an answer to this occurrence alone wins. */
  myResponse?: RsvpStatus;
  /** The occurrence's original start (its id for "this event" edits and answers) when it differs or was answered. */
  at?: number;
  /** This occurrence's own title, when it was changed on its own. */
  title?: string;
  /** It has ended: drawn faded, like Google Calendar (it can still be opened and answered). */
  past: boolean;
};

export type CalendarDay = {
  start: number;
  today: boolean;
  /** Month view: a day of the shown month (others are greyed). */
  inMonth: boolean;
  allDay: CalendarItem[];
  timed: CalendarItem[];
};

export type CalendarViewState = {
  status: "loading" | "ready" | "failed";
  mode: CalendarMode;
  /** Any instant inside the shown period. */
  anchor: number;
  timeZone: string;
  /** 0 = Sunday, 1 = Monday. */
  firstDay: number;
  days: CalendarDay[];
  /** The fetched range. */
  from: number;
  to: number;
  events: CalendarEvent[];
  occurrences: EventOccurrence[];
  /** My other calendars ("My calendars" after my main one; secondary Google calendars are "from Google"). */
  calendars: CalendarInfo[];
  /** My Google Calendar still syncs in (the workspace's Google import has Calendar on and I'm in it). */
  googleSync: boolean;
  busy: boolean;
  /** Loaded, and nothing occurs in the shown days ("Nothing scheduled"). */
  empty: boolean;
  error?: MessageDescriptor;
  /** My last answer didn't go through ("Couldn't send your answer", with Try again: `retryRsvp`). */
  rsvpFailed?: { eventId: string; response: RsvpStatus; at?: number; following?: boolean };
};

/** What the event editor edits (create or change). */
/** `occurrence`: editing one occurrence of a series (its original start), so saving can ask "This event / All events". */
export type EventDraft = CreateEventRequest & { id?: string; occurrence?: number };

export class CalendarViewModel extends ViewModel<CalendarViewState> {
  private generation = 0;
  private calendarsAsked = false;

  constructor(private readonly ws: WorkspaceViewModel, timeZone: string, firstDay = 0) {
    const now = ws.deps.clock.now();
    super({ status: "loading", mode: "week", anchor: now, timeZone, firstDay, days: [], from: now, to: now, events: [], occurrences: [], calendars: [], googleSync: false, busy: false, empty: false });
  }

  private get api() {
    return this.ws.deps.api;
  }

  private get meId(): string {
    return this.ws.state.meId;
  }

  setMode(mode: CalendarMode): void {
    this.update({ mode });
    void this.load();
  }

  today(): void {
    this.update({ anchor: this.ws.deps.clock.now() });
    void this.load();
  }

  /** Previous (-1) or next (+1) day / week / month. */
  step(dir: -1 | 1): void {
    const { mode, anchor, timeZone } = this.state;
    const days = mode === "day" ? 1 : mode === "week" ? 7 : 0;
    let next: number;
    if (days > 0) next = addDays(anchor, dir * days, timeZone);
    else {
      const grid = monthGrid(anchor, timeZone, this.state.firstDay);
      next = dir > 0 ? addDays(grid[14]!, 31, timeZone) : addDays(grid[14]!, -31, timeZone);
    }
    this.update({ anchor: next });
    void this.load();
  }

  /** Jump to the day of `ts` in the current mode (mini-calendar click, a month cell → day). */
  goTo(ts: number, mode?: CalendarMode): void {
    this.update({ anchor: ts, ...(mode === undefined ? {} : { mode }) });
    void this.load();
  }

  async load(): Promise<void> {
    const { from, to } = this.range();
    const gen = ++this.generation;
    this.update({ from, to });
    this.layout();
    if (!this.calendarsAsked) {
      this.calendarsAsked = true;
      void this.loadCalendars();
    }
    try {
      const r = await this.api.listEvents(this.ws.wsId, from, to);
      if (gen !== this.generation || this.isDisposed) return;
      this.update({ status: "ready", events: r.events, occurrences: r.occurrences });
      this.layout();
    } catch {
      if (gen === this.generation && !this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
    }
  }

  /** My other calendars and whether Google Calendar still syncs in (asked once, with the first range); failures leave just my main one. */
  async loadCalendars(): Promise<void> {
    try {
      const r = await this.api.listCalendars(this.ws.wsId);
      if (!this.isDisposed) this.update({ calendars: r.calendars, googleSync: r.googleSync });
    } catch {
      // My main calendar alone.
    }
  }

  /** Realtime: an event changed or went away; re-expand it locally in the shown range. */
  onEvent(ev: PersistentEvent): void {
    if (ev.type === "calendar.event.removed") this.putEvent(undefined, ev.eventId);
    else if (ev.type === "calendar.event.updated") this.putEvent(ev.event.cancelled ? undefined : ev.event, ev.event.id);
  }

  /** A blank draft for a slot (click on the grid); all-day when `allDay`. */
  draftAt(start: number, allDay = false): EventDraft {
    const tz = this.state.timeZone;
    const s = allDay ? startOfDay(start, tz) : start;
    return { title: "", start: s, end: allDay ? addDays(s, 1, tz) : s + 3_600_000, allDay, timeZone: tz, attendees: [], huddle: false, visibility: "attendees", reminderMinutes: defaultReminderMinutes(allDay) };
  }

  /**
   * The draft switched to or from all day: the reminder follows Google's default for the new kind while it was still
   * the default for the old one (10 minutes ↔ the day before at 9:00); a reminder I picked stays.
   */
  withAllDay(d: EventDraft, allDay: boolean): EventDraft {
    const reminderMinutes = d.reminderMinutes === defaultReminderMinutes(d.allDay) ? defaultReminderMinutes(allDay) : d.reminderMinutes;
    const { reminderMinutes: _r, ...rest } = d;
    void _r;
    return { ...rest, allDay, ...(reminderMinutes === undefined ? {} : { reminderMinutes }) };
  }

  /** My reminder for an event (or for one occurrence, by its original start): minutes before, or null for none. */
  reminderOf(e: CalendarEvent, at?: number): number | null {
    return at === undefined ? (e.reminderMinutes ?? null) : occurrenceReminder(e, at);
  }

  /**
   * My reminder for the whole event (null = none) and whether it also comes by email; my answer stays as it is (the
   * organizer's is "accepted").
   */
  async setReminder(eventId: string, minutes: number | null, email?: boolean): Promise<void> {
    const e = this.state.events.find((x) => x.id === eventId);
    if (e === undefined) return;
    const answer = this.myResponse(e) ?? "accepted";
    const { reminderMinutes: _m, reminderEmail: _e, ...rest } = e;
    void _m;
    void _e;
    const mail = email ?? e.reminderEmail;
    this.putEvent({ ...rest, ...(minutes === null ? {} : { reminderMinutes: minutes }), ...(mail === true ? { reminderEmail: true } : {}) }, eventId);
    try {
      this.putEvent(await this.api.rsvpEvent(this.ws.wsId, eventId, answer, minutes, undefined, email === undefined ? undefined : { reminderEmail: email }), eventId);
    } catch {
      void this.load();
    }
  }

  /**
   * "This and following events" for my answer: this occurrence and every later one. Like Google, a guest's answer
   * leaves the organizer's series whole: it's kept as mine from that occurrence on (from the first one, the series).
   */
  async rsvpFollowing(eventId: string, at: number, response: RsvpStatus): Promise<void> {
    const e = this.state.events.find((x) => x.id === eventId);
    if (e !== undefined) {
      const fromFirst = at === e.start;
      const myOccurrenceResponses = fromFirst ? [] : withOccurrenceResponse(e.myOccurrenceResponses, { at, response, following: true });
      const { myOccurrenceResponses: _old, ...rest } = e;
      void _old;
      this.putEvent({ ...rest, ...(fromFirst ? { myResponse: response } : {}), ...(myOccurrenceResponses.length > 0 ? { myOccurrenceResponses } : {}) }, eventId);
    }
    try {
      this.putEvent(await this.api.rsvpEvent(this.ws.wsId, eventId, response, undefined, at, { following: true }), eventId);
      this.clearRsvpFailed();
    } catch {
      this.update({ rsvpFailed: { eventId, response, at, following: true } });
      void this.load();
    }
  }

  /** Saves a draft (create, or update when it has an id). → the event, or undefined when refused. */
  async save(d: EventDraft): Promise<CalendarEvent | undefined> {
    const problems = eventProblems(d);
    if (problems.length > 0) {
      this.update({ error: msg(problems.includes("noTitle") ? "calendar.error.noTitle" : "calendar.error.endBeforeStart") });
      return undefined;
    }
    this.update({ busy: true });
    try {
      let ev: CalendarEvent;
      if (d.id === undefined) {
        const { occurrence: _o, ...create } = d;
        void _o;
        ev = await this.api.createEvent(this.ws.wsId, create);
      } else {
        const { id, reminderMinutes: _r, occurrence: _o, ...rest } = d;
        void _o;
        void _r;
        const { reminderEmail: _m, ...fields } = rest;
        void _m;
        const patch: UpdateEventRequest = { ...fields, description: d.description ?? null, location: d.location ?? null, recurrence: d.recurrence ?? null, color: d.color ?? null };
        ev = await this.api.updateEvent(this.ws.wsId, id, patch);
        ev = await this.applyReminder(ev, d);
      }
      this.putEvent(ev, ev.id);
      this.clearError();
      return ev;
    } catch {
      this.update({ error: msg("calendar.error.saveFailed") });
      return undefined;
    } finally {
      if (!this.isDisposed) this.update({ busy: false });
    }
  }

  async cancel(eventId: string, fromMailboxId?: string): Promise<void> {
    const before = this.state.events.find((e) => e.id === eventId);
    this.putEvent(undefined, eventId);
    try {
      await this.api.deleteEvent(this.ws.wsId, eventId, fromMailboxId);
    } catch {
      if (before !== undefined) this.putEvent(before, eventId);
      this.update({ error: msg("calendar.error.saveFailed") });
    }
  }

  /**
   * "This and following events": the edit applies from this occurrence on (the server splits the series there: it ends
   * before, a new series starts at the occurrence). The range reloads to show both.
   */
  async saveFollowing(d: EventDraft & { id: string; occurrence: number }): Promise<CalendarEvent | undefined> {
    const e = this.state.events.find((x) => x.id === d.id);
    const problems = eventProblems(d);
    if (problems.length > 0) {
      this.update({ error: msg(problems.includes("noTitle") ? "calendar.error.noTitle" : "calendar.error.endBeforeStart") });
      return undefined;
    }
    this.update({ busy: true });
    try {
      const { id, reminderMinutes: _r, reminderEmail: _m, occurrence, ...rest } = d;
      void _r;
      void _m;
      // The rule as the series has it (the server shares out count, exdates and changes); a new rule replaces it.
      const recurrence = d.recurrence === undefined ? null : e?.recurrence !== undefined && sameRule(d.recurrence, e.recurrence) ? undefined : d.recurrence;
      const { recurrence: _x, ...fields } = rest;
      void _x;
      const patch: UpdateEventRequest = { ...fields, following: occurrence, description: d.description ?? null, location: d.location ?? null, color: d.color ?? null, ...(recurrence === undefined ? {} : { recurrence }) };
      const ev = await this.api.updateEvent(this.ws.wsId, id, patch);
      this.clearError();
      await this.load();
      return ev;
    } catch {
      this.update({ error: msg("calendar.error.saveFailed") });
      return undefined;
    } finally {
      if (!this.isDisposed) this.update({ busy: false });
    }
  }

  /** After saving an existing event: my reminder (not part of the event's fields) when the editor changed it. */
  private async applyReminder(ev: CalendarEvent, d: EventDraft): Promise<CalendarEvent> {
    const minutes = d.reminderMinutes ?? null;
    const email = d.reminderEmail === true;
    if (minutes === (ev.reminderMinutes ?? null) && email === (ev.reminderEmail === true)) return ev;
    try {
      return await this.api.rsvpEvent(this.ws.wsId, ev.id, this.myResponse(ev) ?? "accepted", minutes, undefined, { reminderEmail: email });
    } catch {
      return ev;
    }
  }

  /** "Delete this and following events": the series ends before the occurrence at `at` (its original start). */
  async cancelFollowing(eventId: string, at: number, fromMailboxId?: string): Promise<void> {
    try {
      await this.api.deleteEvent(this.ws.wsId, eventId, fromMailboxId, at);
    } catch {
      this.update({ error: msg("calendar.error.saveFailed") });
    }
    await this.load();
  }

  /** My reminder for one occurrence of a series (null = none for it); my answer to it stays as it is. */
  async setOccurrenceReminder(eventId: string, at: number, minutes: number | null): Promise<void> {
    const e = this.state.events.find((x) => x.id === eventId);
    if (e === undefined) return;
    const answer = occurrenceResponse(e.myOccurrenceResponses, at) ?? e.myResponse ?? "accepted";
    this.putEvent({ ...e, myOccurrenceReminders: [...(e.myOccurrenceReminders ?? []).filter((r) => r.at !== at), { at, minutes }] }, eventId);
    try {
      this.putEvent(await this.api.rsvpEvent(this.ws.wsId, eventId, answer, minutes, at), eventId);
    } catch {
      void this.load();
    }
  }

  /**
   * Saves an occurrence edited in the editor: `only` changes just that occurrence (an override); otherwise the whole
   * series takes the edit, its times shifted by however much this occurrence's moved.
   */
  async saveOccurrence(d: EventDraft & { id: string; occurrence: number }, only: boolean): Promise<CalendarEvent | undefined> {
    const e = this.state.events.find((x) => x.id === d.id);
    if (e?.recurrence === undefined) return this.save(d);
    if (!only) {
      const shown = occurrencesOf(e, d.occurrence - DAY * 400, d.occurrence + DAY * 400, 2000).find((o) => (o.at ?? o.start) === d.occurrence);
      const shift = d.start - (shown?.start ?? d.occurrence);
      const length = d.end - d.start;
      const { occurrence: _o, ...series } = d;
      void _o;
      return this.save({ ...series, start: e.start + shift, end: e.start + shift + length, recurrence: d.recurrence ?? e.recurrence });
    }
    const problems = eventProblems(d);
    if (problems.length > 0) {
      this.update({ error: msg(problems.includes("noTitle") ? "calendar.error.noTitle" : "calendar.error.endBeforeStart") });
      return undefined;
    }
    const o: { at: number; start: number; end: number; title?: string; location?: string; description?: string } = { at: d.occurrence, start: d.start, end: d.end };
    if (d.title.trim() !== e.title) o.title = d.title.trim();
    if ((d.location ?? "") !== (e.location ?? "")) o.location = d.location ?? "";
    if ((d.description ?? "") !== (e.description ?? "")) o.description = d.description ?? "";
    const recurrence = withOverride(e.recurrence, o, e.end - e.start);
    this.update({ busy: true });
    try {
      const ev = await this.api.updateEvent(this.ws.wsId, e.id, { recurrence });
      this.putEvent(ev, ev.id);
      this.clearError();
      return ev;
    } catch {
      this.update({ error: msg("calendar.error.saveFailed") });
      return undefined;
    } finally {
      if (!this.isDisposed) this.update({ busy: false });
    }
  }

  /** My answer to one occurrence of a series (by its original start). */
  async rsvpOccurrence(eventId: string, at: number, response: RsvpStatus): Promise<void> {
    const e = this.state.events.find((x) => x.id === eventId);
    if (e !== undefined) this.putEvent({ ...e, myOccurrenceResponses: withOccurrenceResponse(e.myOccurrenceResponses, { at, response }) }, eventId);
    try {
      this.putEvent(await this.api.rsvpEvent(this.ws.wsId, eventId, response, undefined, at), eventId);
      this.clearRsvpFailed();
    } catch {
      this.update({ rsvpFailed: { eventId, response, at } });
      void this.load();
    }
  }

  /** Sends the answer that didn't go through again (the "Try again" of "Couldn't send your answer"). */
  async retryRsvp(): Promise<void> {
    const f = this.state.rsvpFailed;
    if (f === undefined) return;
    this.clearRsvpFailed();
    if (f.at === undefined) await this.rsvp(f.eventId, f.response);
    else if (f.following === true) await this.rsvpFollowing(f.eventId, f.at, f.response);
    else await this.rsvpOccurrence(f.eventId, f.at, f.response);
  }

  /** Dismisses "Couldn't send your answer". */
  clearRsvpFailed(): void {
    if (this.state.rsvpFailed === undefined) return;
    const { rsvpFailed: _f, ...rest } = this.state;
    void _f;
    this.setState(rest);
  }

  /** Remove one occurrence of a series (adds it to `exdates`, dropping any change made to it). */
  async skipOccurrence(eventId: string, occurrenceStart: number): Promise<void> {
    const e = this.state.events.find((x) => x.id === eventId);
    if (e?.recurrence === undefined) return;
    const { overrides, ...rest } = e.recurrence;
    const left = (overrides ?? []).filter((o) => o.at !== occurrenceStart);
    const recurrence = { ...rest, ...(left.length > 0 ? { overrides: left } : {}), exdates: [...(e.recurrence.exdates ?? []), occurrenceStart] };
    this.putEvent({ ...e, recurrence }, eventId);
    try {
      this.putEvent(await this.api.updateEvent(this.ws.wsId, eventId, { recurrence }), eventId);
    } catch {
      void this.load();
    }
  }

  /** My subscription link for other calendar apps (undefined if it couldn't be fetched). */
  async feedLink(reset = false): Promise<{ url: string; webcalUrl: string } | undefined> {
    try {
      return await this.api.calendarFeed(this.ws.wsId, reset);
    } catch {
      return undefined;
    }
  }

  /**
   * A new app password (the others keep working), named `req.label` or for its `req.purpose` ("Mail app",
   * "Calendar app"), or `off` to revoke all of mine here.
   */
  async davPassword(off = false, req?: AppPasswordRequest): Promise<{ server: string; username: string; password: string } | undefined> {
    try {
      return await this.api.calendarDavPassword(this.ws.wsId, off, req);
    } catch {
      return undefined;
    }
  }

  /** The iPhone/Mac profile (Mail, Calendar, Contacts) with a new app password inside, signed by the server; undefined on failure. */
  async appleProfile(label?: string): Promise<{ filename: string; profile: string; signed: boolean } | undefined> {
    try {
      return await this.api.mailAppleProfile(this.ws.wsId, label);
    } catch {
      return undefined;
    }
  }

  async rsvp(eventId: string, response: RsvpStatus): Promise<void> {
    const e = this.state.events.find((x) => x.id === eventId);
    if (e !== undefined) this.putEvent({ ...e, myResponse: response }, eventId);
    try {
      this.putEvent(await this.api.rsvpEvent(this.ws.wsId, eventId, response), eventId);
      this.clearRsvpFailed();
    } catch {
      this.update({ rsvpFailed: { eventId, response } });
      void this.load();
    }
  }

  /**
   * My answer for an event (realtime payloads carry everyone's; I read mine from attendees). Someone outside the
   * workspace organizes an event in my calendar (an invite, an imported Google event): it's still mine to answer.
   */
  myResponse(e: CalendarEvent): RsvpStatus | undefined {
    const external = e.externalOrganizer !== undefined;
    if (e.organizerId === this.meId && !external) return undefined;
    return e.myResponse ?? e.attendees.find((a) => a.userId === this.meId)?.response ?? (external ? "needs_action" : undefined);
  }

  /**
   * I can change or cancel it: I organize it in Bundle. An event someone outside organizes (an invite, an imported
   * Google event) is theirs to change, even in my calendar: I only answer it and set my reminder.
   */
  canEdit(e: CalendarEvent): boolean {
    return e.organizerId === this.meId && e.externalOrganizer === undefined;
  }

  /** The scopes to offer when answering one occurrence of a series ("This event / This and following / All events"). */
  rsvpScopes(eventId: string, at: number): ("this" | "following" | "all")[] {
    return this.occurrenceScopes(eventId, at);
  }

  /** The scopes to offer when saving or deleting one occurrence of a series ("This event / This and following / All events"). */
  occurrenceScopes(eventId: string, at: number): ("this" | "following" | "all")[] {
    const e = this.state.events.find((x) => x.id === eventId);
    if (e?.recurrence === undefined) return [];
    // From the first occurrence "this and following" is all of them.
    return at === e.start ? ["this", "all"] : ["this", "following", "all"];
  }

  private clearError(): void {
    const { error: _e, ...rest } = this.state;
    void _e;
    this.setState(rest);
  }

  private range(): { from: number; to: number } {
    const { mode, anchor, timeZone, firstDay } = this.state;
    if (mode === "day") {
      const from = startOfDay(anchor, timeZone);
      return { from, to: addDays(from, 1, timeZone) };
    }
    if (mode === "week") {
      const days = weekDays(anchor, timeZone, firstDay);
      return { from: days[0]!, to: addDays(days[6]!, 1, timeZone) };
    }
    const grid = monthGrid(anchor, timeZone, firstDay);
    return { from: grid[0]!, to: addDays(grid[41]!, 1, timeZone) };
  }

  private putEvent(e: CalendarEvent | undefined, id: string): void {
    const events = this.state.events.filter((x) => x.id !== id);
    let occurrences = this.state.occurrences.filter((o) => o.eventId !== id);
    if (e !== undefined) {
      const occ = occurrencesOf(e, this.state.from, this.state.to);
      if (occ.length > 0) {
        events.push(e);
        occurrences = [...occurrences, ...occ].sort((a, b) => a.start - b.start);
      }
    }
    this.update({ events, occurrences });
    this.layout();
  }

  private layout(): void {
    const { mode, anchor, timeZone, firstDay, from } = this.state;
    const now = this.ws.deps.clock.now();
    const starts = mode === "day" ? [startOfDay(anchor, timeZone)] : mode === "week" ? weekDays(anchor, timeZone, firstDay) : monthGrid(anchor, timeZone, firstDay);
    const month = new Date(addDays(from, 14, timeZone)).getUTCMonth();
    const byId = new Map(this.state.events.map((e) => [e.id, e]));
    const days: CalendarDay[] = starts.map((start) => {
      const end = addDays(start, 1, timeZone);
      const here = this.state.occurrences.filter((o) => o.start < end && o.end > start);
      const allDay: CalendarItem[] = [];
      const timed: { id: string; start: number; end: number }[] = [];
      const byKey = new Map<string, EventOccurrence>();
      for (const o of here) {
        const e = byId.get(o.eventId);
        if (e === undefined) continue;
        const key = `${o.eventId}:${o.at ?? o.start}`;
        byKey.set(key, o);
        if (e.allDay || o.end - o.start >= DAY) allDay.push(this.item(key, e, o, start, end, 0, 1));
        else timed.push({ id: key, start: o.start, end: o.end });
      }
      const placed = layoutDay(timed).map((p) => {
        const o = byKey.get(p.id)!;
        return this.item(p.id, byId.get(o.eventId)!, o, start, end, p.column, p.columns);
      });
      return { start, today: sameDay(start, now, timeZone), inMonth: mode !== "month" || new Date(start + 12 * 3_600_000).getUTCMonth() === month, allDay, timed: placed };
    });
    this.update({ days, empty: this.state.status === "ready" && days.every((d) => d.allDay.length === 0 && d.timed.length === 0) });
  }

  private item(key: string, e: CalendarEvent, o: EventOccurrence, dayStart: number, dayEnd: number, column: number, columns: number): CalendarItem {
    const s = Math.max(o.start, dayStart);
    const en = Math.min(o.end, dayEnd);
    const it: CalendarItem = { key, event: e, start: o.start, end: o.end, column, columns, top: Math.round((s - dayStart) / 60_000), height: Math.max(15, Math.round((en - s) / 60_000)), past: isPast(o.end, this.ws.deps.clock.now()) };
    const mine = o.myResponse ?? this.myResponse(e);
    if (mine !== undefined) it.myResponse = mine;
    if (o.at !== undefined) it.at = o.at;
    if (o.title !== undefined) it.title = o.title;
    return it;
  }
}
